import React from 'react';
import { Flex, TextV2, ButtonV2, Box } from '@withjoy/joykit';
import type { Property } from 'csstype';
import type { GetContactCollectionByMagicLinkQuery } from '../../../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { StyledText } from '@apps/guest/thanksForRsvp/components/Congratulations/components/Header/Header.styles';
import { useLandingController } from './Landing.controller';
import { getImageSource } from '@apps/guest/contactCollector/shared/utils/getImageSource';
import type { VisualType } from '@apps/guest/contactCollector/shared/constants';
import { ErrorSplash } from '@shared/components/ErrorSplash';
import { Wrapper, Details, Footer } from '../components';
import type { Context } from '../../shared/types';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { addRendition } from '@shared/utils/photoRendition';
import { useTranslation } from '@shared/core';

interface LandingProps
  extends Readonly<{
    data?: GetContactCollectionByMagicLinkQuery;
    setContext: React.Dispatch<React.SetStateAction<Context>>;
  }> {}

export const Landing: React.FC<LandingProps> = ({ data, setContext }) => {
  const { fontFamily, fontTextTransform, eventDisplayName, coupleNames, date, loading, error, handleOnProvideDetailsClick, handleOnVisitEventWebsiteClick, isMobile } =
    useLandingController(setContext, data);

  const { tq } = useTranslation('contactCollector');

  if (loading) return <JoyLogoLoader loaderKey="contact-collector-landing" />;
  if (error) return <ErrorSplash customError={{ title: tq`Hmm… we’re having some trouble.`, subtitle: tq`This page may have changed or is temporarily unavailable.` }} />;

  const headerImageSource = getImageSource(data?.getContactCollectionByMagicLink?.mediaType as VisualType, data?.getContactCollectionByMagicLink?.mediaSource ?? '');

  return (
    <Wrapper>
      <img
        alt="source"
        src={addRendition({ url: headerImageSource, renditionSize: 'medium' })}
        width="100%"
        style={{ borderRadius: isMobile ? 0 : '12px' }}
      ></img>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX="24px"
      >
        <StyledText
          typographyVariant="hed2"
          marginTop="3rem"
          fontSize={['30px', null, '40px']}
          fontFamily={fontFamily}
          textTransform={fontTextTransform as Property.TextTransform}
          $withMargin={false}
        >
          {eventDisplayName}
        </StyledText>
        {data?.getContactCollectionByMagicLink?.eventDateDisplayEnabled && date && (
          <TextV2
            typographyVariant="body2"
            marginTop="8px"
          >
            {date}
          </TextV2>
        )}
        <TextV2
          typographyVariant="hed6"
          marginTop="24px"
          textAlign="center"
        >
          {data?.getContactCollectionByMagicLink?.message}
        </TextV2>
        <Box marginTop="1rem">
          <Details coupleNames={coupleNames} />
        </Box>
        <ButtonV2
          intent="primary"
          shape="rounded"
          marginTop="32px"
          onClick={handleOnProvideDetailsClick}
          width="100%"
          cursor="pointer"
        >
          {tq`Provide Details`}
        </ButtonV2>
        {data?.getContactCollectionByMagicLink?.websiteLinkEnabled && (
          <ButtonV2
            cursor="pointer"
            onClick={handleOnVisitEventWebsiteClick}
            variant="link"
            marginTop="32px"
            color="mono14"
            fontWeight={600}
            width={['100%', null, '327px']}
          >
            {tq`Visit Event Website`}
          </ButtonV2>
        )}
        <Footer />
      </Flex>
    </Wrapper>
  );
};
